import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  GestureResponderEvent,
  NativeSyntheticEvent,
  Platform,
  Pressable,
  PointerEvent as RNPointerEvent,
  StyleProp,
  TextInput,
  TextInputKeyPressEventData,
  TextInput as TextInputType,
  View,
  ViewStyle,
} from 'react-native';
import {
  handleWebKeyPress,
  hasSystemModifierKey,
  Key,
} from '@platform/shortcuts/utils/keyboard-platform';
import { Icons, Tooltip } from '@usepepper/sprout-kit-ui';
import {
  isTransientNumericValue,
  parseTransientAwareNumber,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/numeric-input';
import { KeyboardShortcutBadge } from '@sales-portal/components/keyboard-shortcut-badge';
import {
  PricingTableInputHistoryContext,
  PricingTableInputPopover,
} from './pricing-table-input-popover';
import {
  type PricingTableQuickAction,
  PricingTableQuickActionsPopover,
  QUICK_ACTIONS_SHORTCUT_KEY,
} from './pricing-table-quick-actions-popover';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useDebouncedCallback } from 'use-debounce';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';

export type InputValidationResult =
  | { valid: true }
  | { message: string; valid: false };

export enum PricingTableInputPopoverKind {
  QuickActions = 'QUICK_ACTIONS',
  QuickActionsWithHistory = 'QUICK_ACTIONS_WITH_HISTORY',
}

export type PricingTableInputPopoverConfig =
  | {
      kind: PricingTableInputPopoverKind.QuickActions;
      selectedCount?: number;
    }
  | {
      currencyCode: string;
      historyContext: PricingTableInputHistoryContext;
      /** False while the table is still scrolling the input into place. */
      isAnchorSettled?: boolean;
      kind: PricingTableInputPopoverKind.QuickActionsWithHistory;
    };

interface BaseQuickActionPopoverProps {
  isQuickActionsOpen: boolean;
  onActionPressIn: (event: GestureResponderEvent) => void;
  onActionSelect: (action: PricingTableQuickAction) => void;
  onClearPopoverInteraction: () => void;
  quickActions: PricingTableQuickAction[];
  trigger: React.ReactNode;
}

interface QuickActionsPopoverVariantProps extends BaseQuickActionPopoverProps {
  onCloseQuickActions: () => void;
  selectedCount?: number;
}

interface QuickActionsWithHistoryPopoverVariantProps
  extends Omit<BaseQuickActionPopoverProps, 'trigger'> {
  anchorRef: React.RefObject<View | null>;
  currencyCode: string;
  historyContext: PricingTableInputHistoryContext;
  isAnchorSettled: boolean;
  isDismissed: boolean;
  onActionPointerDown: (event: RNPointerEvent) => void;
  onDismiss: () => void;
}

interface Props {
  bookmarked?: boolean;
  bookmarkNote?: string | null;
  containerStyle?: StyleProp<ViewStyle>;
  decimalPlaces: number;
  disabled?: boolean;
  inputID?: string;
  onArrowNavigation?: (direction: 'up' | 'down') => void;
  onBlur: (value: string) => void;
  onChangeValue?: (value: string) => void;
  onFocus?: () => void;
  onFocusChange?: (isFocused: boolean) => void;
  onQuickActionsOpenChange?: (open: boolean) => void;
  originalValue: string;
  placeholder?: string;
  popoverProps: PricingTableInputPopoverConfig;
  quickActions: PricingTableQuickAction[];
  quickActionsOpen?: boolean;
  updatedValue: string | null;
  validate?: (value: string) => InputValidationResult;
}

const QuickActionsPopoverVariant: React.FC<QuickActionsPopoverVariantProps> = ({
  isQuickActionsOpen,
  onActionPressIn,
  onActionSelect,
  onClearPopoverInteraction,
  onCloseQuickActions,
  quickActions,
  selectedCount,
  trigger,
}) => {
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        onClearPopoverInteraction();
        onCloseQuickActions();
      }
    },
    [onClearPopoverInteraction, onCloseQuickActions],
  );

  return (
    <PricingTableQuickActionsPopover
      actions={quickActions}
      isOpen={isQuickActionsOpen}
      onActionPressIn={onActionPressIn}
      onActionSelect={onActionSelect}
      onOpenChange={handleOpenChange}
      selectedCount={selectedCount}
      trigger={trigger}
    />
  );
};

// Mounted only while the input is focused: the table holds one of these per
// editable cell, and an idle Tamagui popover is still a costly subtree.
const QuickActionsWithHistoryPopoverVariant: React.FC<
  QuickActionsWithHistoryPopoverVariantProps
> = ({
  anchorRef,
  currencyCode,
  historyContext,
  isAnchorSettled,
  isDismissed,
  isQuickActionsOpen,
  onActionPointerDown,
  onActionPressIn,
  onActionSelect,
  onClearPopoverInteraction,
  onDismiss,
  quickActions,
}) => {
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next) {
        return;
      }
      onClearPopoverInteraction();
      // Native has no outside-tap blur, so hide without dropping focus or the keyboard
      if (Platform.OS !== 'web') {
        onDismiss();
      }
    },
    [onClearPopoverInteraction, onDismiss],
  );

  return (
    <PricingTableInputPopover
      anchorRef={anchorRef}
      currencyCode={currencyCode}
      historyContext={historyContext}
      isOpen={
        isAnchorSettled &&
        !isDismissed &&
        (historyContext.enabled || isQuickActionsOpen)
      }
      isQuickActionsOpen={isQuickActionsOpen}
      onActionPointerDown={onActionPointerDown}
      onActionPressIn={onActionPressIn}
      onActionSelect={onActionSelect}
      onOpenChange={handleOpenChange}
      quickActions={quickActions}
    />
  );
};

const formatDisplayValue = (val: string, decimalPlaces: number): string => {
  if (!val) {
    return val;
  }

  const num = Number(val);
  return isNaN(num) ? val : num.toFixed(decimalPlaces);
};

const VALIDATION_DEBOUNCE_MS = 500;

export const PricingTableInput = React.memo<Props>(
  ({
    bookmarkNote,
    bookmarked,
    containerStyle,
    decimalPlaces,
    disabled,
    inputID,
    onArrowNavigation,
    onBlur,
    onChangeValue,
    onFocus,
    onFocusChange,
    onQuickActionsOpenChange,
    originalValue,
    placeholder,
    popoverProps,
    quickActions,
    quickActionsOpen,
    updatedValue,
    validate,
  }) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isPopoverDismissed, setIsPopoverDismissed] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [hasValidationError, setHasValidationError] = useState(false);
    const [uncontrolledQuickActionsOpen, setUncontrolledQuickActionsOpen] =
      useState(false);
    const isControlled = quickActionsOpen !== undefined;
    const isQuickActionsOpen = isControlled
      ? quickActionsOpen
      : uncontrolledQuickActionsOpen;
    const setIsQuickActionsOpen = useCallback(
      (next: boolean) => {
        if (isControlled) {
          onQuickActionsOpenChange?.(next);
        } else {
          setUncontrolledQuickActionsOpen(next);
        }
      },
      [isControlled, onQuickActionsOpenChange],
    );
    const [value, setValue] = useState(
      formatDisplayValue(originalValue, decimalPlaces),
    );
    const inputRef = useRef<TextInputType>(null);
    const anchorRef = useRef<View>(null);
    const didSelectQuickActionRef = useRef(false);
    const skipBlurCommitRef = useRef(false);
    const validationToastIdRef = useRef<number | null>(null);

    const { dismissToast, showToast } = useExperienceToasts();

    const runValidation = useCallback(
      (nextText: string): boolean => {
        if (typeof validationToastIdRef.current === 'number') {
          dismissToast(validationToastIdRef.current);
          validationToastIdRef.current = null;
        }

        if (!validate) {
          setHasValidationError(false);
          return false;
        }

        if (isTransientNumericValue(nextText)) {
          setHasValidationError(false);
          return false;
        }

        const result = validate(nextText);

        if (!result.valid) {
          setHasValidationError(true);
          validationToastIdRef.current = showToast({
            onDismiss: () => {
              setHasValidationError(false);
              validationToastIdRef.current = null;
            },
            title: result.message,
            variant: 'error',
          });
          return true;
        }

        setHasValidationError(false);
        return false;
      },
      [dismissToast, showToast, validate],
    );

    const debouncedValidate = useDebouncedCallback(
      runValidation,
      VALIDATION_DEBOUNCE_MS,
    );

    useEffect(() => {
      if (updatedValue !== null) {
        setValue(formatDisplayValue(updatedValue, decimalPlaces));
      } else if (!isFocused || didSelectQuickActionRef.current) {
        setValue(formatDisplayValue(originalValue, decimalPlaces));
      }
      didSelectQuickActionRef.current = false;
    }, [decimalPlaces, isFocused, originalValue, updatedValue]);

    const [styles, theme] = useStyles();

    const handleFocus = useCallback(() => {
      setIsFocused(true);
      setIsPopoverDismissed(false);
      onFocusChange?.(true);
      onFocus?.();
    }, [onFocus, onFocusChange]);

    const handleBlur = useCallback(() => {
      if (skipBlurCommitRef.current) {
        return;
      }

      const hasTransientNumericValue = isTransientNumericValue(value);

      let hasValidationFailure = false;
      if (validate && isDirty && !hasTransientNumericValue) {
        debouncedValidate.cancel();
        hasValidationFailure = runValidation(value);
      }

      setIsFocused(false);
      onFocusChange?.(false);

      if (hasValidationFailure) {
        const restoredValue = formatDisplayValue(
          updatedValue ?? originalValue,
          decimalPlaces,
        );
        setValue(restoredValue);
        onChangeValue?.(restoredValue);
        return;
      }

      if (isDirty && !hasTransientNumericValue) {
        onBlur(value);
      }

      const displayValue = hasTransientNumericValue
        ? (updatedValue ?? originalValue)
        : value;
      const formattedValue = formatDisplayValue(displayValue, decimalPlaces);

      onChangeValue?.(formattedValue);
      setValue(formattedValue);
    }, [
      debouncedValidate,
      decimalPlaces,
      isDirty,
      onBlur,
      onChangeValue,
      onFocusChange,
      originalValue,
      runValidation,
      updatedValue,
      validate,
      value,
    ]);

    const handleChangeText = useCallback(
      (text: string) => {
        const nextText = text.trim();
        const isValidInput =
          isTransientNumericValue(nextText) ||
          parseTransientAwareNumber(nextText) !== null;

        if (isValidInput) {
          setIsDirty(true);
          setValue(nextText);
          onChangeValue?.(nextText);

          if (validate) {
            debouncedValidate(nextText);
          }
        }
      },
      [debouncedValidate, onChangeValue, validate],
    );

    const closeQuickActions = useCallback(() => {
      setIsQuickActionsOpen(false);
    }, [setIsQuickActionsOpen]);

    const openQuickActions = useCallback(() => {
      if (quickActions.length === 0) {
        return;
      }
      setIsPopoverDismissed(false);
      setIsQuickActionsOpen(true);
    }, [quickActions, setIsQuickActionsOpen]);

    const handleQuickActionSelect = useCallback(
      (action: PricingTableQuickAction) => {
        if (action.disabled) {
          return;
        }

        didSelectQuickActionRef.current = true;
        setIsDirty(false);
        action.onSelect();
        inputRef.current?.focus();
        skipBlurCommitRef.current = false;
      },
      [],
    );

    const handleActionPressIn = useCallback((event: GestureResponderEvent) => {
      event.preventDefault();
      skipBlurCommitRef.current = true;
    }, []);

    const handleHistoryActionPointerDown = useCallback(
      (event: RNPointerEvent) => {
        event.preventDefault();
        skipBlurCommitRef.current = true;
        inputRef.current?.focus();
      },
      [],
    );

    const clearPopoverInteraction = useCallback(() => {
      skipBlurCommitRef.current = false;
    }, []);

    const dismissPopover = useCallback(() => {
      setIsPopoverDismissed(true);
      closeQuickActions();
    }, [closeQuickActions]);

    const handleInputKeyPress = useCallback(
      (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
        handleWebKeyPress(e);

        // Cmd/Ctrl means the user is reaching for a browser shortcut
        // (Cmd+A selects all), not the quick action on that letter.
        if (hasSystemModifierKey(e)) {
          return;
        }

        const key = e.nativeEvent.key;
        const hasQuickActions = quickActions.length > 0;

        if (
          key.toUpperCase() === QUICK_ACTIONS_SHORTCUT_KEY &&
          hasQuickActions
        ) {
          e.preventDefault?.();

          if (isQuickActionsOpen) {
            closeQuickActions();
            return;
          }

          openQuickActions();
          return;
        }

        if (key === Key.Escape) {
          e.preventDefault?.();
          closeQuickActions();
          inputRef.current?.blur();
          return;
        }

        if (isQuickActionsOpen && hasQuickActions) {
          const upperKey = key.toUpperCase();
          const matchedAction = quickActions.find(
            (action) =>
              !action.disabled &&
              action.shortcutKey?.toUpperCase() === upperKey,
          );
          if (matchedAction) {
            e.preventDefault?.();
            didSelectQuickActionRef.current = true;
            setIsDirty(false);
            matchedAction.onSelect();
            return;
          }
        }

        if (
          (key === Key.ArrowDown || key === Key.ArrowUp) &&
          onArrowNavigation
        ) {
          e.preventDefault?.();
          onArrowNavigation(key === Key.ArrowDown ? 'down' : 'up');
        }
      },
      [
        closeQuickActions,
        isQuickActionsOpen,
        onArrowNavigation,
        openQuickActions,
        quickActions,
      ],
    );

    if (disabled) {
      return (
        <View style={styles.container}>
          <View style={[styles.input, containerStyle]}>
            <TextInput
              editable={false}
              style={[styles.inputField, styles.inputFieldDisabled]}
              testID="new-pricing-value"
              value={formatDisplayValue(
                updatedValue ?? originalValue,
                decimalPlaces,
              )}
            />
          </View>
        </View>
      );
    }

    const inputBox = (
      <View
        collapsable={false}
        ref={anchorRef}
        style={[
          styles.input,
          isFocused ? styles.inputFocused : null,
          hasValidationError ? styles.inputError : null,
          containerStyle,
        ]}
      >
        {bookmarked && !isFocused ? (
          <Tooltip placement="top">
            <Tooltip.Trigger>
              <View style={styles.bookmarkIcon}>
                <Icons.Bookmark
                  color={theme.sk.color.icon.secondary}
                  size={14}
                />
              </View>
            </Tooltip.Trigger>
            <Tooltip.Content>
              <Typography
                color={theme.sk.color.text.ondark}
                variant="paragraph-sm"
              >
                {bookmarkNote || i18n.t('pricing.bookmarks.bookmarked_tooltip')}
              </Typography>
            </Tooltip.Content>
          </Tooltip>
        ) : quickActions.length > 0 ? (
          <Pressable
            // Absorb pointer down events to prevent closing a popover
            onPointerDown={(event) => event.preventDefault()}
            onPress={(event) => {
              event.stopPropagation?.();
              inputRef.current?.focus();
              if (isFocused && isQuickActionsOpen) {
                closeQuickActions();
              } else {
                openQuickActions();
              }
            }}
            style={styles.quickActionShortcut}
            tabIndex={-1}
          >
            <KeyboardShortcutBadge
              shortcut={QUICK_ACTIONS_SHORTCUT_KEY}
              size={theme.sk.typography.paragraph.sm.lineHeight}
            />
          </Pressable>
        ) : null}
        <TextInput
          autoComplete="off"
          id={inputID}
          // Android's numeric input type swallows the quick-action shortcut keys
          keyboardType={Platform.OS === 'android' ? 'default' : 'decimal-pad'}
          onBlur={handleBlur}
          onChangeText={handleChangeText}
          onFocus={handleFocus}
          onKeyPress={handleInputKeyPress}
          placeholder={placeholder}
          placeholderTextColor={theme.sk.color.text.tertiary}
          ref={inputRef}
          style={styles.inputField}
          testID={inputID ?? 'new-pricing-value'}
          value={value}
        />
      </View>
    );

    return (
      <Pressable
        // Absorb click events to prevent propagation to parent table rows
        onPress={(event) => event.stopPropagation?.()}
        style={styles.container}
        tabIndex={-1}
      >
        {popoverProps.kind === PricingTableInputPopoverKind.QuickActions ? (
          <QuickActionsPopoverVariant
            isQuickActionsOpen={isQuickActionsOpen}
            onActionPressIn={handleActionPressIn}
            onActionSelect={handleQuickActionSelect}
            onClearPopoverInteraction={clearPopoverInteraction}
            onCloseQuickActions={closeQuickActions}
            quickActions={quickActions}
            selectedCount={popoverProps.selectedCount}
            trigger={inputBox}
          />
        ) : (
          <>
            {inputBox}
            {isFocused ? (
              <QuickActionsWithHistoryPopoverVariant
                anchorRef={anchorRef}
                currencyCode={popoverProps.currencyCode}
                historyContext={popoverProps.historyContext}
                isAnchorSettled={popoverProps.isAnchorSettled ?? true}
                isDismissed={isPopoverDismissed}
                isQuickActionsOpen={isQuickActionsOpen}
                onActionPointerDown={handleHistoryActionPointerDown}
                onActionPressIn={handleActionPressIn}
                onActionSelect={handleQuickActionSelect}
                onClearPopoverInteraction={clearPopoverInteraction}
                onDismiss={dismissPopover}
                quickActions={quickActions}
              />
            ) : null}
          </>
        )}
      </Pressable>
    );
  },
);

const useStyles = createStyles((theme) => ({
  bookmarkIcon: {
    alignItems: 'center',
    height: 16,
    justifyContent: 'center',
    marginLeft: theme.sk.spacing.spaceXsmall,
    width: 16,
  },
  container: {
    alignItems: 'flex-end',
    position: 'relative',
  },
  input: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusSmall,
    borderWidth: 1,
    flexDirection: 'row',
    height: 32,
    width: 88,
  },
  inputError: {
    borderColor: theme.sk.color.text.error,
  },
  inputField: {
    ...theme.sk.typography.paragraph.md,
    alignSelf: 'stretch',
    color: theme.sk.color.text.primary,
    flex: 1,
    fontWeight: 400,
    includeFontPadding: false, // fix for Android line height
    lineHeight: undefined, // fix for iOS line height
    minWidth: 0,
    paddingLeft: theme.sk.spacing.spaceXsmall,
    paddingRight: theme.sk.spacing.spaceSMedium,
    paddingVertical: 0,
    textAlign: 'right',
    textAlignVertical: 'center',
  },
  inputFieldDisabled: {
    color: theme.sk.color.text.tertiary,
  },
  inputFocused: {
    borderColor: theme.sk.color.border.input.active,
  },
  quickActionShortcut: {
    marginLeft: theme.sk.spacing.spaceXsmall,
  },
}));
